/**
 * Risk Profile system for the Advanced Retirement Calculator
 *
 * A risk profile defines the dispersion/tail behaviour of annual returns
 * around the expected mean. Built-in profiles cover the standard spectrum
 * from "Capital Preservation" to "Speculative / Very Aggressive".
 *
 * Users may also create custom profiles.  All built-in profiles are
 * read-only but can be duplicated into a custom copy.
 *
 * @module domain/retirement/riskProfiles
 */

import type { Language } from "~/utils/language";

// NOTE: We do NOT import from ./constants to avoid circular dependency
// (constants.ts imports from riskProfiles.ts). Use plain string keys
// that match AssetCategory enum values.

// ============================================================================
// TYPES
// ============================================================================

/**
 * A risk profile defines how far annual returns may deviate from the mean.
 *
 * percentileLow / percentileHigh define the probability mass that lies
 * outside the downside / upside deltas (default 5 % / 95 %).
 *
 * distributionType is always "normal_truncated" for now.
 */
export interface RiskProfile {
  /** Unique identifier (slug for built-ins, uuid for custom) */
  id: string;
  /** Human-readable name */
  name: string;
  /** Lower percentile boundary (default 0.05 = 5th percentile) */
  percentileLow: number;
  /** Upper percentile boundary (default 0.95 = 95th percentile) */
  percentileHigh: number;
  /** How far below the mean the percentileLow return sits (percentage points, >= 0) */
  downsideDeltaPct: number;
  /** How far above the mean the percentileHigh return sits (percentage points, >= 0) */
  upsideDeltaPct: number;
  /** Distribution family used for sampling */
  distributionType: "normal_truncated";
  /** Whether this profile ships with the product (read-only) */
  isBuiltIn: boolean;
  /** ISO timestamp of creation */
  createdAt?: string;
  /** ISO timestamp of last update */
  updatedAt?: string;
}

// ============================================================================
// BUILT-IN PROFILES
// ============================================================================

/** z-score for 5th / 95th percentile of the standard normal */
export const Z_SCORE_5_95 = 1.6448536269514729; // ≈ 1.64485

/**
 * Built-in risk profiles calibrated from historical market data.
 *
 * downsideDeltaPct / upsideDeltaPct define how far the 5th and 95th
 * percentile annual returns sit below / above the expected mean.
 *
 * Calibration sources & rationale (all figures annualized):
 *
 * ┌───────────────────────┬──────┬──────┬────────┬─────────────────────────────────────┐
 * │ Profile               │  ↓%  │  ↑%  │  σ≈    │ Basis                               │
 * ├───────────────────────┼──────┼──────┼────────┼─────────────────────────────────────┤
 * │ static                │   0  │   0  │  0.0%  │ Cash, no volatility                 │
 * │ solid                 │  14  │  14  │  8.5%  │ Bonds + balanced portfolio (30/70)   │
 * │ growth                │  33  │  30  │ 19.1%  │ S&P 500 σ≈16-20%, worst -37% (2008) │
 * │ aggressive            │  48  │  50  │ 29.8%  │ Single tech stocks σ≈30-40%          │
 * │ speculative           │  80  │ 120  │ 60.8%  │ Bitcoin σ≈60-80%, worst -73% (2018) │
 * └───────────────────────┴──────┴──────┴────────┴─────────────────────────────────────┘
 *
 * Key historical reference points:
 * - S&P 500: σ≈16-20%, worst years -43% (1931), -37% (2008), -35% (1937)
 * - REITs:   σ≈18-22%, worst -37% (2008), range -16% to +29% typical
 * - Bitcoin: σ≈60-80%, worst -73% (2018), -64% (2022), best +1318% (2017)
 * - Bonds:   σ≈5-7%,   worst -13% (2022), usually +2% to +8%
 * - 60/40:   σ≈10-13%, worst -22% (2008), -16% (2022)
 */
export const BUILT_IN_RISK_PROFILES: readonly RiskProfile[] = [
  {
    id: "static",
    name: "סטטי (ללא סיכון)",
    percentileLow: 0.05,
    percentileHigh: 0.95,
    downsideDeltaPct: 0,
    upsideDeltaPct: 0,
    distributionType: "normal_truncated",
    isBuiltIn: true,
  },
  {
    id: "solid",
    name: "סולידי",
    percentileLow: 0.05,
    percentileHigh: 0.95,
    downsideDeltaPct: 14,
    upsideDeltaPct: 14,
    distributionType: "normal_truncated",
    isBuiltIn: true,
  },
  {
    id: "growth",
    name: "צמיחה",
    percentileLow: 0.05,
    percentileHigh: 0.95,
    downsideDeltaPct: 33,
    upsideDeltaPct: 30,
    distributionType: "normal_truncated",
    isBuiltIn: true,
  },
  {
    id: "aggressive",
    name: "אגרסיבי",
    percentileLow: 0.05,
    percentileHigh: 0.95,
    downsideDeltaPct: 48,
    upsideDeltaPct: 50,
    distributionType: "normal_truncated",
    isBuiltIn: true,
  },
  {
    id: "speculative",
    name: "ספקולטיבי",
    percentileLow: 0.05,
    percentileHigh: 0.95,
    downsideDeltaPct: 80,
    upsideDeltaPct: 120,
    distributionType: "normal_truncated",
    isBuiltIn: true,
  },
] as const;

// ============================================================================
// DEFAULT PROFILE MAPPING PER CATEGORY
// ============================================================================

/**
 * Which built-in risk profile each asset category gets by default.
 * Keys are AssetCategory enum values (plain strings to avoid circular import).
 */
export const DEFAULT_PROFILE_BY_CATEGORY: Record<string, string> = {
  CASH: "static",
  DEPOSIT: "solid",
  PENSION: "solid",
  REALESTATE: "growth",
  STOCKS: "growth",
  RSU: "aggressive",
  CRYPTO: "speculative",
  OTHER: "solid",
  DEBT: "static",
};

// ============================================================================
// PROFILE NAME TRANSLATIONS
// ============================================================================

/** English display names for built-in risk profiles, keyed by profile id. */
const PROFILE_NAME_ENG: Record<string, string> = {
  static: "Static (No Risk)",
  solid: "Conservative",
  growth: "Growth",
  aggressive: "Aggressive",
  speculative: "Speculative",
};

const PROFILE_NAME_RUS: Record<string, string> = {
  static: "Статичный (без риска)",
  solid: "Консервативный",
  growth: "Рост",
  aggressive: "Агрессивный",
  speculative: "Спекулятивный",
};

/**
 * Get the display name for a risk profile, translated to the given language.
 * For built-in profiles, returns the translated name; for custom profiles,
 * returns the user-supplied name as-is.
 */
export function getProfileDisplayName(
  profile: RiskProfile,
  lang: Language = "HEB"
): string {
  if (profile.isBuiltIn && lang === "ENG" && PROFILE_NAME_ENG[profile.id]) {
    return PROFILE_NAME_ENG[profile.id];
  }
  if (profile.isBuiltIn && lang === "RUS" && PROFILE_NAME_RUS[profile.id]) {
    return PROFILE_NAME_RUS[profile.id];
  }
  return profile.name;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Look up a risk profile by id, searching built-ins first then custom list.
 */
export function findProfileById(
  id: string,
  customProfiles: RiskProfile[] = []
): RiskProfile | undefined {
  return (
    BUILT_IN_RISK_PROFILES.find((p) => p.id === id) ??
    customProfiles.find((p) => p.id === id)
  );
}

/**
 * Derive the Normal sigma (standard deviation, as a decimal) from a risk
 * profile's delta values.
 *
 * sigmaLow  = downsideDelta / z
 * sigmaHigh = upsideDelta   / z
 * sigma     = average of the two
 *
 * The result is in decimal form (e.g. 0.15 for 15 %).
 */
export function deriveSigmaFromProfile(profile: RiskProfile): number {
  const downsideDelta = profile.downsideDeltaPct / 100;
  const upsideDelta = profile.upsideDeltaPct / 100;
  const sigmaLow = downsideDelta / Z_SCORE_5_95;
  const sigmaHigh = upsideDelta / Z_SCORE_5_95;
  return (sigmaLow + sigmaHigh) / 2;
}

/**
 * Compute the implied sigmaPct (as a percentage number, not decimal) for
 * a risk profile.  Used when comparing profiles for migration.
 */
export function impliedSigmaPctFromProfile(profile: RiskProfile): number {
  return ((profile.downsideDeltaPct + profile.upsideDeltaPct) / 2) / Z_SCORE_5_95;
}

/**
 * Compute the truncation bounds for a given mean return and risk profile.
 *
 * minR = max(-1.0, mean - downsideDelta)
 * maxR = mean + upsideDelta
 */
export function boundsFromProfile(
  meanDecimal: number,
  profile: RiskProfile
): { minR: number; maxR: number } {
  const downsideDelta = profile.downsideDeltaPct / 100;
  const upsideDelta = profile.upsideDeltaPct / 100;
  return {
    minR: Math.max(-1.0, meanDecimal - downsideDelta),
    maxR: meanDecimal + upsideDelta,
  };
}

/**
 * Display-friendly p5 / p95 returns for a given mean and profile.
 * Returns values as decimals (e.g. -0.14 and 0.21).
 */
export function displayP5P95(
  meanDecimal: number,
  profile: RiskProfile
): { p5: number; p95: number } {
  const { minR, maxR } = boundsFromProfile(meanDecimal, profile);
  return { p5: minR, p95: maxR };
}

// ============================================================================
// LEGACY PROFILE ID MIGRATION
// ============================================================================

/**
 * Maps old (removed) built-in profile IDs to their replacement in the
 * reduced 5-profile set.  Used when loading persisted settings that may
 * reference the old IDs.
 */
const LEGACY_PROFILE_ID_MAP: Record<string, string> = {
  "capital-preservation": "solid",
  "conservative": "solid",
  "moderately-cautious": "solid",
  "balanced": "solid",
  "moderate-growth": "growth",
  "aggressive-growth": "aggressive",
};

/**
 * Migrate a persisted profile ID to the current set.
 * Returns the original id if it still exists, otherwise the mapped replacement.
 */
export function migrateProfileId(id: string): string {
  // If the id exists in the current built-in set, keep it
  if (BUILT_IN_RISK_PROFILES.some((p) => p.id === id)) return id;
  return LEGACY_PROFILE_ID_MAP[id] ?? id;
}

// ============================================================================
// SAMPLING
// ============================================================================

/**
 * Sample a single annual return from a truncated Normal distribution.
 *
 * Uses Box-Muller for the normal sample, then resamples up to K=8 times
 * if the value falls outside [minR, maxR].  If still out of bounds after
 * K attempts, clamps to the nearest bound.
 *
 * @param meanDecimal  Expected return as decimal (e.g. 0.06 for 6%)
 * @param profile      The risk profile controlling dispersion
 * @returns            Sampled annual return as a decimal
 */
export function sampleAnnualReturn(
  meanDecimal: number,
  profile: RiskProfile
): number {
  const sigma = deriveSigmaFromProfile(profile);
  const { minR, maxR } = boundsFromProfile(meanDecimal, profile);

  // If sigma is effectively zero, return mean (clamped)
  if (sigma < 1e-12) {
    return Math.max(minR, Math.min(maxR, meanDecimal));
  }

  const MAX_RESAMPLE = 8;

  for (let attempt = 0; attempt < MAX_RESAMPLE; attempt++) {
    const u1 = Math.random();
    const u2 = Math.random();
    const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    const r = meanDecimal + sigma * z;

    if (r >= minR && r <= maxR) {
      return r;
    }
  }

  // Fallback: clamp
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  const r = meanDecimal + sigma * z;
  return Math.max(minR, Math.min(maxR, r));
}

/**
 * Sample a portfolio-weighted annual return.
 *
 * For each category with a positive weight, independently sample an annual
 * return using `sampleAnnualReturn`, then combine them by weight.
 */
export function sampleWeightedReturn(
  categoryWeights: Record<string, number>,
  returnAssumptions: Record<string, { expectedReturn: number; riskProfileId?: string }>,
  customProfiles: RiskProfile[] = []
): number {
  const total = Object.values(categoryWeights).reduce((s, v) => s + v, 0);
  if (total <= 0) return 0;

  let weightedReturn = 0;

  for (const [category, value] of Object.entries(categoryWeights)) {
    if (value <= 0) continue;
    const weight = value / total;
    const assumption = returnAssumptions[category];
    if (!assumption) continue;

    const profileId = assumption.riskProfileId ?? DEFAULT_PROFILE_BY_CATEGORY[category];
    const profile = profileId ? findProfileById(profileId, customProfiles) : undefined;

    if (profile) {
      const r = sampleAnnualReturn(assumption.expectedReturn, profile);
      weightedReturn += weight * r;
    } else {
      // Fallback: deterministic
      weightedReturn += weight * assumption.expectedReturn;
    }
  }

  return weightedReturn;
}

// ============================================================================
// MIGRATION
// ============================================================================

/**
 * Map a legacy standard-deviation percentage to the closest built-in
 * risk profile.
 *
 * Computes each built-in's implied sigmaPct and picks the one with the
 * smallest absolute difference to `legacySigmaPct`.
 *
 * @param legacySigmaPct  The old standard deviation as a percentage
 *                        (e.g. 18 for 18 %).
 * @returns               The id of the closest built-in profile.
 */
export function mapLegacySigmaToProfile(legacySigmaPct: number): string {
  let bestId = BUILT_IN_RISK_PROFILES[0].id;
  let bestDiff = Infinity;

  for (const profile of BUILT_IN_RISK_PROFILES) {
    const implied = impliedSigmaPctFromProfile(profile);
    const diff = Math.abs(legacySigmaPct - implied);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestId = profile.id;
    }
  }

  return bestId;
}
